import {
  prepararAccion,
  type Accion,
  type ArticuloAccion,
  type NombreAccion,
} from "@zociety/core";

/**
 * Por dónde salen las acciones de la aplicación.
 *
 * **Hoy no salen a ningún lado, y es el diseño.** El catálogo, la validación y
 * los puntos donde se emite ya están puestos; lo único que falta para encender
 * la analítica es cambiar `destino` por uno que hable con el proveedor. Esa
 * separación es la razón de construir el vocabulario antes que la tubería:
 * instrumentar es el trabajo laborioso, y así se hace una sola vez.
 *
 * **Encenderlo no es una decisión técnica.** Registrar el comportamiento de
 * personas identificadas exige aviso de privacidad por la LFPDPPP, y eso lo
 * aprueba la dueña del negocio. Hasta entonces el destino no envía nada, y el
 * sistema funciona igual.
 */

export interface DestinoAnalitica {
  nombre: string;
  enviar(accion: Accion): void;
}

/**
 * El destino de hoy: valida y descarta.
 *
 * No es un hueco pendiente de rellenar sino un estado válido. En pruebas y en
 * los emuladores tampoco habría que enviar nada, así que este destino se queda
 * incluso después de encender la analítica en producción.
 */
const destinoNulo: DestinoAnalitica = {
  nombre: "ninguno",
  /*
   * En desarrollo se ve por consola, y no es un adorno.
   *
   * Sin esto, instrumentar es escribir a ciegas: no hay forma de comprobar que
   * una acción se emite en el momento correcto, con los parámetros correctos y
   * una sola vez. Los duplicados por un `useEffect` mal atado no se notarían
   * hasta mirar un reporte inflado meses después.
   */
  enviar: (a) => {
    if (import.meta.env.DEV) {
      console.info(`analítica → ${a.nombre}`, a.parametros, a.articulos ?? "");
    }
  },
};

let destino: DestinoAnalitica = destinoNulo;

/** Cambia a dónde van las acciones. Es el único punto que toca la opción 3. */
export function usarDestino(nuevo: DestinoAnalitica): void {
  destino = nuevo;
}

export function destinoActual(): string {
  return destino.nombre;
}

/**
 * Registra algo que la persona hizo.
 *
 * **Nunca lanza y nunca se espera**, igual que `reportar`. Se llama en el camino
 * feliz de la aplicación, y una analítica que rompe una compra convierte una
 * medición en una pérdida.
 *
 * Lo que la validación descarte se avisa solo en desarrollo. En producción sería
 * ruido para quien usa la aplicación, y quien tiene que enterarse es quien
 * escribe el código: por eso lo que de verdad lo impide son las pruebas del core.
 */
export function registrarAccion(
  nombre: NombreAccion,
  parametros: Record<string, unknown> = {},
  articulos?: ArticuloAccion[]
): void {
  try {
    const { accion, problemas } = prepararAccion(nombre, parametros, articulos);

    if (import.meta.env.DEV && problemas.length > 0) {
      console.warn(`analítica: «${nombre}» se emitió incompleta`, problemas);
    }
    if (accion) destino.enviar(accion);
  } catch {
    /* Una medición jamás debe tumbar la pantalla que estaba midiendo. */
  }
}
